/* eslint-disable @typescript-eslint/no-explicit-any */
import { IRootState } from 'store'
import { AiAgentMessage } from '../ai-agent.type'

export type ContextType = 'selections' | 'filters' | 'user'
export type FeatureToggleType = 'followUpQuestions'

interface ContextConfig {
  type: ContextType
  enabled: boolean
  label: string
  prefix: string
}

interface FeatureToggleConfig {
  type: FeatureToggleType
  enabled: boolean
  label: string
  adminOnly: boolean
}

/**
 * Builds context string based on context type
 */
export function buildContextString(state: IRootState, contextType: ContextType): string | null {
  const { comparison, auth } = state
  const contextParts: string[] = []

  if (contextType === 'selections') {
    if (comparison.selectedProductClassCover?.className) {
      const className = comparison.selectedProductClassCover.className
      const classId = comparison.selectedProductClassCover.classId
      contextParts.push(`Class: ${className} (ID: ${classId})`)

      if (comparison.selectedProductClassCover.coverType?.length) {
        const coverTypes = Array.isArray(comparison.selectedProductClassCover.coverType)
          ? comparison.selectedProductClassCover.coverType
          : [comparison.selectedProductClassCover.coverType]
        const coverIds = comparison.selectedProductClassCover.coverId || []
        contextParts.push(`Cover Types: ${coverTypes.join(', ')} (IDs: ${coverIds.join(', ')})`)
      }
    }

    const selectedPolicies = comparison.selectedPolicyContents
      .filter((policy: any) => policy.policy)
      .map(
        (policy: any) =>
          `${policy.policy.name} (Policy ID: ${policy.policy.id} | Policy Code: ${policy.policy.code})`,
      )

    if (selectedPolicies.length > 0) {
      contextParts.push(`Selected Policies: ${selectedPolicies.join(', ')}`)
    }

    const selectedSectionCovers = comparison.selectedSectionCovers
      .filter((cover: any) => cover.id)
      .map((cover: any) => `Section Cover ID: ${cover.id}`)

    if (selectedSectionCovers.length > 0) {
      contextParts.push(`Selected Section Covers: ${selectedSectionCovers.join(', ')}`)
    }
  } else if (contextType === 'filters') {
    if (comparison.filter.compareBy) {
      contextParts.push(`Comparison Mode: ${comparison.filter.compareBy}`)
    }

    if (comparison.filter.preview) {
      contextParts.push(`Preview: ${comparison.filter.preview}`)
    }

    if (comparison.filter.category) {
      contextParts.push(`Category: ${comparison.filter.category}`)
    }

    if (comparison.filter.showNew !== undefined) {
      contextParts.push(`Show New: ${comparison.filter.showNew ? 'Yes' : 'No'}`)
    }

    if (comparison.filter.searchFeature) {
      contextParts.push(`Search: ${comparison.filter.searchFeature}`)
    }

    const featureFilters = Object.values(comparison.filter.filterFeature).flat()
    if (featureFilters.length > 0) {
      contextParts.push(`Feature Filters: ${featureFilters.join(', ')}`)
    }

    const policyFilters = Object.values(comparison.filter.filterPolicy).flat()
    if (policyFilters.length > 0) {
      contextParts.push(`Policy Filters: ${policyFilters.join(', ')}`)
    }

    if (comparison.filter.effectiveDate) {
      contextParts.push(`Effective Date: ${comparison.filter.effectiveDate}`)
    }
  } else if (contextType === 'user') {
    if (auth.account) {
      const { account } = auth

      if (account.name) {
        contextParts.push(`User: ${account.name}`)
      }
      if (account.email) {
        contextParts.push(`Email: ${account.email}`)
      }

      if (account.company) {
        contextParts.push(`Company: ${account.company.name} (ID: ${account.company.id})`)
      }

      if (account.buy_group) {
        const buyGroup = account.buy_group
        contextParts.push(`Buy Group: ${buyGroup.name} (ID: ${buyGroup.id})`)
        contextParts.push(`Country: ${buyGroup.country_name} (${buyGroup.country_code})`)
      }

      if (account.is_admin !== undefined) {
        contextParts.push(`Admin Access: ${account.is_admin ? 'Yes' : 'No'}`)
      }
      if (account.is_viewing_room !== undefined) {
        contextParts.push(`Viewing Room: ${account.is_viewing_room ? 'Yes' : 'No'}`)
      }

      if (account.is_admin && comparison.impersonation.selectedBuyGroup) {
        contextParts.push(`Impersonating Buy Group: ${comparison.impersonation.selectedBuyGroup}`)
        if (comparison.impersonation.selectedCountry) {
          contextParts.push(`Impersonating Country: ${comparison.impersonation.selectedCountry}`)
        }
      }
    }
  }

  return contextParts.length > 0 ? contextParts.join(' | ') : null
}

/**
 * Extracts policy codes from the current state
 */
export function extractPolicyCodes(state: IRootState): string[] {
  const { comparison } = state

  const policyCodes: string[] = []

  comparison.selectedPolicyContents.forEach((policyContent: any) => {
    if (policyContent.policy?.code) {
      policyCodes.push(policyContent.policy.code)
    }
  })

  return policyCodes
}

/**
 * Extracts insurance class from the current state
 */
export function extractInsuranceClass(state: IRootState): string {
  const { comparison } = state

  if (comparison.selectedProductClassCover?.className) {
    return comparison.selectedProductClassCover.className
  }

  return ''
}

/**
 * Creates context messages based on configuration
 */
export function createContextMessages(
  state: IRootState,
  configs: ContextConfig[],
): AiAgentMessage[] {
  const messages: AiAgentMessage[] = []

  configs.forEach((config) => {
    if (config.enabled) {
      const contextString = buildContextString(state, config.type)
      if (contextString) {
        messages.push({
          id: `${config.type}-context-${Date.now()}`,
          sender: 'assistant',
          text: `${config.prefix}: ${contextString}`,
          timestamp: Date.now(),
        })
      }
    }
  })

  return messages
}

/**
 * Check if user is admin
 */
export function isUserAdmin(state: IRootState): boolean {
  return state.auth.account?.is_admin === true
}

export type { ContextConfig, FeatureToggleConfig }
